export type Project = {
  domain: string;
  /** Optional public website override. Use null when a project has no public URL yet. */
  websiteUrl?: string | null;
  /** Human-readable surface label when the project is not represented by a domain. */
  displayDomain?: string;
  status?: "live" | "in-development";
  title: string;
  category: string;
  description: string;
  stack: string[];
  /** Product runtime surfaces; a promotional site alone does not add a Web App platform. */
  platforms?: string[];
  productTypes?: string[];
  languages?: string[];
  appStoreUrl?: string;
  destinations?: Array<{ label: string; href: string; kind: "web" | "ios" | "android" }>;
  hue: number;
};

const rawProjects: Omit<Project, "hue">[] = [
  {
    domain: "agrofounder.com",
    title: "AgroFounder",
    category: "Agricultural operations AI",
    description: "A Brazilian farm-operations platform that captures field evidence, reconciles execution against plans and inventory, and turns exceptions into owner-ready decisions through a private AI assistant.",
    stack: ["Next.js", "TypeScript", "SQLite", "OpenAI"],
    platforms: ["Web App", "Field Tablet"],
    productTypes: ["Farm Operations", "Record & Reconcile", "Private AI Assistant"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open AgroFounder", href: "https://agrofounder.com", kind: "web" },
      { label: "Try the real product demo", href: "https://agrofounder.com/demo", kind: "web" },
      { label: "See commercial plans", href: "https://agrofounder.com/pricing", kind: "web" },
    ],
  },
  {
    domain: "astracam",
    websiteUrl: null,
    displayDomain: "iOS app · in development",
    status: "in-development",
    title: "AstraCAM",
    category: "Telescope camera companion",
    description: "A native iPhone companion in development for telescope capture, observing sessions, equipment notes, Sky Guide and a private on-device astronomy journal.",
    stack: ["Swift", "SwiftUI", "AVFoundation", "StoreKit"],
    platforms: ["iOS"],
    productTypes: ["Telescope Camera", "Observation Journal", "Sky Guide"],
    languages: ["EN"],
  },
  {
    domain: "australleather.com",
    title: "Austral Leather",
    category: "B2B materials platform",
    description: "A bilingual wholesale-leather platform connecting Argentine material, color and performance specifications with sampling, quoting and global export workflows.",
    stack: ["React", "TypeScript", "Vinext", "Cloudflare"],
    platforms: ["Website"],
    productTypes: ["B2B Catalog", "Export Commerce", "Material System"],
    languages: ["ES", "EN"],
    destinations: [
      { label: "Open Austral Leather", href: "https://australleather.com", kind: "web" },
      { label: "Explore materials", href: "https://australleather.com/#materials", kind: "web" },
      { label: "Browse the color library", href: "https://australleather.com/#colors", kind: "web" },
    ],
  },
  { domain: "aulasdeia.com", title: "Aulas de IA", category: "AI education", description: "A Portuguese AI learning platform built with Next.js, PostgreSQL and Stripe to connect protected courses, progress, certificates and reusable resources.", stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"], platforms: ["Web App"], productTypes: ["AI Education", "Course Platform", "Commerce"], languages: ["PT-BR"] },
  { domain: "aiclases.com", title: "AI Clases", category: "AI education", description: "A Spanish AI learning system using Next.js, PostgreSQL and Stripe for structured paths, student progress, certificates and paid access.", stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"], platforms: ["Web App"], productTypes: ["AI Education", "Course Platform", "Commerce"], languages: ["ES"] },
  {
    domain: "antesdepois.com",
    title: "AntesDepois",
    category: "Visual AI studio",
    description: "A preset-driven AI image platform built with Next.js and PostgreSQL, with original/result comparison, credit state and Stripe-backed access.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Before & After", "AI Photo Studio"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open AntesDepois", href: "https://antesdepois.com", kind: "web" },
      { label: "Explore 100 presets", href: "https://antesdepois.com/presets", kind: "web" },
      { label: "See pricing", href: "https://antesdepois.com/precos", kind: "web" },
    ],
  },
  {
    domain: "artmuseumbrasil.com",
    title: "Art Museum Brasil",
    category: "Culture",
    description: "A browser-native 3D museum built with React Three Fiber and Three.js, coordinating rooms, cameras and artwork focus through reusable interaction state.",
    stack: ["Next.js", "TypeScript", "Three.js", "React"],
    platforms: ["iOS", "Android", "Web App"],
    productTypes: ["3D Museum", "Culture"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open web museum", href: "https://artmuseumbrasil.com", kind: "web" },
      { label: "Download for iOS", href: "https://apps.apple.com/br/app/artmuseum-brasil/id6769566273", kind: "ios" },
      { label: "Download for Android", href: "https://play.google.com/store/apps/details?id=com.codeaustral.artmuseumbrasil", kind: "android" },
    ],
  },
  {
    domain: "avatarmatic.com",
    title: "Avatarmatic",
    category: "Realtime avatar platform",
    description: "A PHP and MySQL video-avatar platform combining OpenAI agents, Anam and D-ID rendering, embeddable conversation rooms, recordings and Stripe-backed workspaces.",
    stack: ["PHP", "MySQL", "OpenAI", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Video Avatar Studio", "Realtime Conversations"],
    languages: ["EN"],
    destinations: [
      { label: "Open Avatarmatic", href: "https://avatarmatic.com", kind: "web" },
      { label: "Try the interactive demo", href: "https://avatarmatic.com/#demo", kind: "web" },
      { label: "Browse video templates", href: "https://avatarmatic.com/templates.html", kind: "web" },
      { label: "Open the avatar studio", href: "https://avatarmatic.com/dash/videoai.php", kind: "web" },
    ],
  },
  {
    domain: "bunbandit.com",
    title: "Bun Bandit",
    category: "Native game",
    description: "A native SwiftUI and Metal recipe runner with touch-first controls, deterministic ingredient goals, StoreKit progression and App Store distribution.",
    stack: ["Swift", "SwiftUI", "Metal", "StoreKit"],
    platforms: ["iOS"],
    productTypes: ["Recipe Runner", "Native Game"],
    languages: ["EN", "ES", "PT-BR", "+7"],
    destinations: [{ label: "Open Bun Bandit", href: "https://bunbandit.com", kind: "web" }],
  },
  {
    domain: "callturbo.com",
    title: "CallTurbo",
    category: "AI call operations",
    description: "An Express and MySQL call-operations platform connecting voice agents, visual call flows, inbound and outbound campaigns, SIP, SMS, CRM records and live WebSocket state.",
    stack: ["Node.js", "MySQL", "WebSocket", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI Call Center", "Voice Agents", "CRM & Campaigns"],
    languages: ["EN"],
    destinations: [
      { label: "Open CallTurbo", href: "https://callturbo.com", kind: "web" },
      { label: "Explore call solutions", href: "https://callturbo.com/solutions", kind: "web" },
      { label: "See the visual flow builder", href: "https://callturbo.com/features/visual-call-flow-builder", kind: "web" },
      { label: "Open the workspace", href: "https://callturbo.com/user/login", kind: "web" },
    ],
  },
  {
    domain: "codeaustral.com",
    title: "Code Austral",
    category: "Software engineering",
    description: "A Next.js and Cloudflare studio website built around a reusable technical case-study system, structured service taxonomy, responsive interactions and edge-delivered content.",
    stack: ["Next.js", "TypeScript", "Tailwind CSS", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["Software Studio", "Product Design & AI"],
    languages: ["EN"],
  },
  {
    domain: "cursalo.com",
    title: "Cursalo",
    category: "Learning platform",
    description: "A Laravel and MySQL learning platform organizing 141 Spanish courses into guided routes, authenticated progress and Stripe-backed enrollment.",
    stack: ["Laravel", "PHP", "MySQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Course Platform", "Learning Routes"],
    languages: ["ES"],
    destinations: [
      { label: "Open Cursalo", href: "https://cursalo.com", kind: "web" },
      { label: "Explore the catalog", href: "https://cursalo.com/#catalogo", kind: "web" },
      { label: "Browse free courses", href: "https://cursalo.com/classes?free=on", kind: "web" },
    ],
  },
  { domain: "developargentina.com", title: "Develop Argentina", category: "Software company", description: "A Next.js, Node and PostgreSQL commercial platform structuring software services, AI automation, resources and a territory-led visual archive for Argentine businesses.", stack: ["Next.js", "TypeScript", "Node.js", "PostgreSQL"], platforms: ["Web App"], productTypes: ["Software Studio", "AI & Automation"], languages: ["ES"] },
  {
    domain: "eateasier.com",
    title: "Eat Easier",
    category: "Food system",
    description: "A Swift meal-planning app and Next.js commerce platform connecting AI food scanning, weekly planning, recipes, groceries and a physical Bento product through shared data workflows.",
    stack: ["Swift", "Next.js", "PostgreSQL", "OpenAI"],
    platforms: ["iOS", "Web App"],
    productTypes: ["Meal Planning", "Physical Product"],
    languages: ["EN", "ES"],
    appStoreUrl: "https://apps.apple.com/us/app/eat-easier/id6770307414",
    destinations: [
      { label: "Open Eat Easier", href: "https://eateasier.com", kind: "web" },
      { label: "Download for iOS", href: "https://apps.apple.com/us/app/eat-easier/id6770307414", kind: "ios" },
    ],
  },
  {
    domain: "eaxy.ai",
    title: "Eaxy",
    category: "AI creation studio",
    description: "A modular AI workspace for images, video, voice, writing and agents, packaging repeatable creation workflows behind one fast web interface.",
    stack: ["Next.js", "OpenAI", "TypeScript", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["AI Creation Studio", "Generative Media", "Agents"],
    languages: ["EN"],
    destinations: [{ label: "Open Eaxy", href: "https://eaxy.ai", kind: "web" }],
  },
  {
    domain: "foodphoto.ai",
    title: "FoodPhoto AI",
    category: "Restaurant AI",
    description: "A native Swift and Next.js imaging system that connects camera-aware editing, OpenAI generation, project state and Stripe access across iOS and web.",
    stack: ["Swift", "Next.js", "OpenAI", "Stripe"],
    platforms: ["iOS", "Web App"],
    productTypes: ["Restaurant AI", "Photo Studio"],
    languages: ["EN", "ES"],
    appStoreUrl: "https://apps.apple.com/br/app/foodphoto-ai/id6757116173",
    destinations: [
      { label: "Open web app", href: "https://foodphoto.ai", kind: "web" },
      { label: "Download for iOS", href: "https://apps.apple.com/br/app/foodphoto-ai/id6757116173", kind: "ios" },
    ],
  },
  {
    domain: "fotoprofissa.com",
    title: "FotoProfissa",
    category: "AI portrait studio",
    description: "A WooCommerce and OpenAI portrait pipeline that validates uploads, generates use-specific images and connects Brazilian checkout to managed delivery.",
    stack: ["WordPress", "WooCommerce", "OpenAI", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI Portraits", "Photo Commerce"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open FotoProfissa", href: "https://fotoprofissa.com", kind: "web" },
      { label: "Compare transformations", href: "https://fotoprofissa.com/#transformacoes", kind: "web" },
      { label: "See photo packages", href: "https://fotoprofissa.com/#planos", kind: "web" },
    ],
  },
  {
    domain: "foodelopers.com",
    title: "Foodelopers",
    category: "Food software studio",
    description: "A food-tech engineering studio spanning Laravel backends, Next.js interfaces, Flutter apps and Stripe commerce for ordering, POS, marketplaces and restaurant SaaS.",
    stack: ["Laravel", "Next.js", "Flutter", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Food Software", "Product Studio"],
    languages: ["EN", "ES"],
    destinations: [
      { label: "Open Foodelopers", href: "https://foodelopers.com", kind: "web" },
      { label: "Try Forkit", href: "https://forkit.foodelopers.com", kind: "web" },
      { label: "Try Plateform", href: "https://plateform.foodelopers.com", kind: "web" },
      { label: "Try Orderly", href: "https://orderly.foodelopers.com", kind: "web" },
    ],
  },
  {
    domain: "genetiqo.com",
    title: "Genetiqo Vault",
    category: "Private health journal",
    description: "A privacy-first SwiftUI health journal using on-device state, controlled HealthKit imports and StoreKit access to produce appointment-ready treatment summaries.",
    stack: ["Swift", "SwiftUI", "HealthKit", "StoreKit"],
    platforms: ["iOS"],
    productTypes: ["Treatment Journal", "Private Health"],
    languages: ["EN"],
    destinations: [{ label: "Open Genetiqo Vault", href: "https://genetiqo.com", kind: "web" }],
  },
  {
    domain: "huntorizon.com",
    title: "Huntorizon",
    category: "Native action game",
    description: "A dark-fantasy road hunter for iPhone and iPad with procedural encounters, touch movement, ranged combat and a native Swift game runtime.",
    stack: ["Swift", "SwiftUI", "SpriteKit", "Metal"],
    platforms: ["iOS"],
    productTypes: ["Action RPG", "Procedural Runner", "Native Game"],
    languages: ["EN"],
    destinations: [{ label: "Open Huntorizon", href: "https://huntorizon.com", kind: "web" }],
  },
  {
    domain: "iaespacio.com",
    title: "IA Espacio",
    category: "Interior AI",
    description: "A React and OpenAI redesign workflow embedded in WordPress, connecting photo input, generation modes, result state and Stripe-backed access.",
    stack: ["WordPress", "React", "OpenAI", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Interior AI", "Design Studio"],
    languages: ["ES"],
    destinations: [
      { label: "Open IA Espacio", href: "https://iaespacio.com", kind: "web" },
      { label: "Open Studio", href: "https://iaespacio.com/workspace/", kind: "web" },
    ],
  },
  {
    domain: "iglesiajesus.com",
    title: "Iglesia de Jesús",
    category: "Digital church",
    description: "A Next.js and PostgreSQL faith platform connecting prayer, Bible reading, devotionals, guided plans, membership and Stripe-supported community flows.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Digital Church", "Prayer & Bible"],
    languages: ["ES", "PT-BR"],
    destinations: [
      { label: "Open Iglesia de Jesús", href: "https://iglesiajesus.com", kind: "web" },
      { label: "Pray now", href: "https://iglesiajesus.com/orar", kind: "web" },
      { label: "Read the Bible", href: "https://iglesiajesus.com/biblia", kind: "web" },
    ],
  },
  {
    domain: "jogaia.com",
    title: "JogaIA",
    category: "Browser arcade",
    description: "A Vite and TypeScript browser arcade with four original Three.js/WebGL games, shared input patterns, responsive canvases and instant no-install delivery.",
    stack: ["Three.js", "TypeScript", "WebGL", "Vite"],
    platforms: ["Web Game", "Mobile Web"],
    productTypes: ["3D Arcade", "Four Original Games"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open JogaIA", href: "https://jogaia.com", kind: "web" },
      { label: "Choose a game", href: "https://jogaia.com/#jogos", kind: "web" },
    ],
  },
  {
    domain: "jornaldaia.com",
    title: "Jornal da IA",
    category: "AI newsroom",
    description: "A WordPress and MySQL editorial system for daily Portuguese AI coverage, with PHP content workflows and Cloudflare-cached delivery.",
    stack: ["WordPress", "PHP", "MySQL", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["AI Journalism", "Editorial Platform"],
    languages: ["PT-BR"],
    destinations: [{ label: "Open Jornal da IA", href: "https://jornaldaia.com", kind: "web" }],
  },
  {
    domain: "kublaro.com",
    title: "Kublaro",
    category: "Generative Media",
    description: "A Next.js AI media platform that classifies each brief, routes it to a specialized generation model and manages credits and Stripe access in one workflow.",
    stack: ["Next.js", "TypeScript", "OpenAI", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI Studio", "Image & Video"],
    languages: ["EN", "ES", "PT-BR"],
    destinations: [{ label: "Open Kublaro", href: "https://kublaro.com", kind: "web" }],
  },
  {
    domain: "linkmas.com",
    title: "Link Más",
    category: "Audience platform",
    description: "A PHP, MySQL and Redis link platform combining bio pages, measurable redirects, editable QR codes, campaign analytics and Stripe subscriptions.",
    stack: ["PHP", "MySQL", "Redis", "Stripe"],
    platforms: ["Web App", "PWA"],
    productTypes: ["Creator Platform", "Links & QR"],
    languages: ["ES", "EN"],
    destinations: [{ label: "Open Link Más", href: "https://linkmas.com", kind: "web" }],
  },
  {
    domain: "logoahora.com",
    title: "LogoAhora",
    category: "AI identity studio",
    description: "A Next.js identity-generation system using Recraft for vector output, structured comparison and refinement flows, and Stripe credit-based access.",
    stack: ["Next.js", "TypeScript", "Recraft", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI Logo Studio", "Brand Identity"],
    languages: ["ES"],
    destinations: [
      { label: "Open LogoAhora", href: "https://logoahora.com", kind: "web" },
      { label: "Create a logo", href: "https://logoahora.com/registro", kind: "web" },
    ],
  },
  {
    domain: "makobits.com",
    title: "MakoBits Merge",
    category: "Native puzzle game",
    description: "A native SwiftUI and SpriteKit merge game with deterministic board state, route objectives, GameKit integration and a persistent 60-character collection.",
    stack: ["Swift", "SwiftUI", "SpriteKit", "GameKit"],
    platforms: ["iOS"],
    productTypes: ["Merge Puzzle", "Collectible Game"],
    languages: ["EN", "ES", "PT-BR"],
    appStoreUrl: "https://apps.apple.com/us/app/makobits-merge/id6769057342",
    destinations: [
      { label: "Open MakoBits", href: "https://makobits.com", kind: "web" },
      { label: "Download for iOS", href: "https://apps.apple.com/us/app/makobits-merge/id6769057342", kind: "ios" },
    ],
  },
  {
    domain: "manualdelrestaurante.com",
    title: "Manual del Restaurante",
    category: "Restaurant operations",
    description: "A Next.js operations platform that routes restaurant symptoms into structured procedures, passwordless knowledge paths and Stripe-backed fixed-scope consulting.",
    stack: ["Next.js", "TypeScript", "Stripe", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["Operations Library", "Async Consulting"],
    languages: ["ES"],
    destinations: [
      { label: "Open Manual del Restaurante", href: "https://manualdelrestaurante.com", kind: "web" },
      { label: "Explore the free library", href: "https://manualdelrestaurante.com/biblioteca", kind: "web" },
      { label: "Compare consulting plans", href: "https://manualdelrestaurante.com/consultoria", kind: "web" },
      { label: "Open the cost-control manual", href: "https://manualdelrestaurante.com/guia/control-de-costos", kind: "web" },
    ],
  },
  {
    domain: "matrixagencia.com",
    title: "Matrix Agencia",
    category: "Interactive growth studio",
    description: "A Next.js growth-studio platform combining Three.js/WebGL experiences, GSAP motion, measurable lead paths and automation-oriented campaign architecture.",
    stack: ["Next.js", "TypeScript", "Three.js", "GSAP"],
    platforms: ["Web App"],
    productTypes: ["Interactive Marketing", "Web 3D & Automation"],
    languages: ["ES", "EN"],
    destinations: [{ label: "Open Matrix Agencia", href: "https://matrixagencia.com", kind: "web" }],
  },
  {
    domain: "menucrafters.com",
    title: "MenuCrafters",
    category: "Restaurant product",
    description: "A Next.js and PostgreSQL menu system that turns a restaurant brief into structured content, live editing, hosted publishing, persistent QR assets and Stripe access.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI Menu Studio", "QR & Print"],
    languages: ["EN"],
    destinations: [
      { label: "Open MenuCrafters", href: "https://menucrafters.com", kind: "web" },
      { label: "Start building", href: "https://menucrafters.com/build", kind: "web" },
      { label: "Browse templates", href: "https://menucrafters.com/templates", kind: "web" },
    ],
  },
  {
    domain: "minibicho.com",
    title: "MiniBicho",
    category: "Native pet universe",
    description: "A playful iOS pet universe with collectible companions, living rooms, care actions and arcade mini-games inside a tactile native experience.",
    stack: ["React", "TypeScript", "Three.js", "StoreKit"],
    platforms: ["iOS", "Android"],
    productTypes: ["Virtual Pet", "Arcade Mini-games", "Collectibles"],
    languages: ["PT-BR"],
    destinations: [{ label: "Open MiniBicho", href: "https://minibicho.com", kind: "web" }],
  },
  {
    domain: "nollei.com",
    title: "Nollei",
    category: "Design commerce",
    description: "A Next.js design-commerce system synchronizing 309 made-to-order products with Printify while Stripe handles checkout across 15 original visual collections.",
    stack: ["Next.js", "TypeScript", "Printify", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Home Decor", "Made-to-order Commerce"],
    languages: ["EN"],
    destinations: [{ label: "Open Nollei", href: "https://nollei.com", kind: "web" }],
  },
  {
    domain: "orcanahora.com",
    title: "OrçaNaHora",
    category: "Proposal software",
    description: "A Next.js and PostgreSQL proposal PWA connecting service scoping, live pricing, client review, acceptance, recoverable history and Stripe-powered AI assistance.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App", "PWA"],
    productTypes: ["Proposal Builder", "Optional AI"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open OrçaNaHora", href: "https://orcanahora.com", kind: "web" },
      { label: "Create a free proposal", href: "https://orcanahora.com/novo", kind: "web" },
      { label: "Browse proposal models", href: "https://orcanahora.com/modelos", kind: "web" },
      { label: "See optional AI pricing", href: "https://orcanahora.com/precos", kind: "web" },
    ],
  },
  {
    domain: "pitchdeckgenie.com",
    title: "PitchDeckGenie",
    category: "Fundraising software",
    description: "A Next.js, PostgreSQL and OpenAI fundraising workspace that turns founder context into stage-aware narratives, editable decks, evidence diagnostics and PPTX/PDF exports.",
    stack: ["Next.js", "PostgreSQL", "OpenAI", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Fundraising Workspace", "Deck Generation"],
    languages: ["EN"],
    destinations: [
      { label: "Open PitchDeckGenie", href: "https://pitchdeckgenie.com", kind: "web" },
      { label: "Enter the Studio", href: "https://pitchdeckgenie.com/v2", kind: "web" },
      { label: "Explore generated decks", href: "https://pitchdeckgenie.com/demos", kind: "web" },
      { label: "See pricing", href: "https://pitchdeckgenie.com/pricing", kind: "web" },
    ],
  },
  {
    domain: "postraptor.com",
    title: "PostRaptor",
    category: "Brand-to-social AI",
    description: "A Next.js creative pipeline using Playwright for site extraction and Satori for deterministic social rendering, with reusable brand state and Stripe access.",
    stack: ["Next.js", "Playwright", "Satori", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Brand Intelligence", "Social Creative"],
    languages: ["EN"],
    destinations: [
      { label: "Open PostRaptor", href: "https://postraptor.com", kind: "web" },
      { label: "Run the interactive demo", href: "https://postraptor.com/demo", kind: "web" },
      { label: "See the $9 plan", href: "https://postraptor.com/pricing", kind: "web" },
    ],
  },
  {
    domain: "propbotstudio.com",
    title: "PropBot Studio",
    category: "Real estate AI",
    description: "A Node and OpenAI real-estate automation platform connecting public chat, voice simulation and lead-operations demos behind Cloudflare delivery.",
    stack: ["JavaScript", "Node.js", "OpenAI", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["AI Agents", "CRM & Automation"],
    languages: ["EN", "ES", "PT-BR"],
    destinations: [
      { label: "Open PropBot Studio", href: "https://propbotstudio.com", kind: "web" },
      { label: "Try the AI chatbot", href: "https://propbotstudio.com/demo/chatbot/", kind: "web" },
      { label: "Explore the CRM dashboard", href: "https://propbotstudio.com/demo/dashboard/", kind: "web" },
      { label: "Run the voice-agent demo", href: "https://propbotstudio.com/demo/voice/", kind: "web" },
    ],
  },
  {
    domain: "qrloja.com",
    title: "QRLoja",
    category: "Local commerce platform",
    description: "A Next.js 16 and PostgreSQL storefront platform connecting QR discovery, catalog, checkout, native scheduling and webhook-confirmed payments in one tenant-safe product.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["QR Storefronts", "Orders & Scheduling"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open QRLoja", href: "https://qrloja.com", kind: "web" },
      { label: "Explore live stores", href: "https://qrloja.com/exemplos", kind: "web" },
      { label: "Open Padaria demo", href: "https://qrloja.com/loja/padaria-sol-da-vila", kind: "web" },
      { label: "Open Studio demo", href: "https://qrloja.com/loja/studio-aurora", kind: "web" },
    ],
  },
  {
    domain: "radardeia.com",
    title: "Radar de IA",
    category: "AI newsroom",
    description: "A WordPress and MySQL Spanish AI newsroom with PHP editorial workflows, structured source context and Cloudflare-cached daily distribution.",
    stack: ["WordPress", "PHP", "MySQL", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["AI Journalism", "Editorial Platform"],
    languages: ["ES"],
    destinations: [{ label: "Open Radar de IA", href: "https://radardeia.com", kind: "web" }],
  },
  { domain: "restaurantargentina.com", title: "Restaurant Argentina", category: "Restaurant operations", description: "A WordPress operations platform with JavaScript financial calculators, supplier discovery and WooCommerce/Mercado Pago commerce for the Argentine restaurant market.", stack: ["WordPress", "WooCommerce", "JavaScript", "Mercado Pago"], platforms: ["Web App"], productTypes: ["Restaurant Education", "Digital Commerce"], languages: ["ES"] },
  {
    domain: "restaurantmargin.com",
    title: "Restaurant Margin",
    category: "Restaurant intelligence",
    description: "A Next.js restaurant-intelligence workspace that computes food cost and contribution margin in the browser, persists menu scenarios locally, routes AI assessments through DeepSeek and gates exports and multi-menu workflows with Stripe.",
    stack: ["Next.js", "TypeScript", "DeepSeek", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Restaurant Intelligence", "Menu Engineering"],
    languages: ["EN"],
    destinations: [
      { label: "Open RestaurantMargin", href: "https://restaurantmargin.com", kind: "web" },
      { label: "Open the free workspace", href: "https://restaurantmargin.com/dashboard", kind: "web" },
      { label: "Try the AI advisor", href: "https://restaurantmargin.com/advisor", kind: "web" },
      { label: "Browse free guides", href: "https://restaurantmargin.com/books", kind: "web" },
    ],
  },
  {
    domain: "roomrenovation.ai",
    title: "Room Renovation AI",
    category: "Interior AI",
    description: "A native Swift and Next.js renovation system using OpenAI for room-aware generation, shared project workflows and Stripe-backed plans across iOS and web.",
    stack: ["Swift", "Next.js", "OpenAI", "Stripe"],
    platforms: ["iOS", "Web App"],
    productTypes: ["Interior AI", "Design Tool"],
    languages: ["EN"],
  },
  {
    domain: "rockclash.com",
    title: "RockClash",
    category: "Native music game",
    description: "A native SwiftUI rhythm game using AVFoundation for generation, playback and lane synchronization, with StoreKit progression and export workflows.",
    stack: ["Swift", "SwiftUI", "AVFoundation", "StoreKit"],
    platforms: ["iOS"],
    productTypes: ["Rhythm Game", "Song Creator"],
    languages: ["EN", "ES", "PT-BR", "+7"],
    appStoreUrl: "https://apps.apple.com/us/app/rockclash/id6776700940",
    destinations: [
      { label: "Open RockClash", href: "https://rockclash.com", kind: "web" },
      { label: "Download for iOS", href: "https://apps.apple.com/us/app/rockclash/id6776700940", kind: "ios" },
    ],
  },
  {
    domain: "skullsailor.com",
    title: "Skull Sailor",
    category: "Native pirate game",
    description: "A landscape-native pirate adventure set around Florianopolis, combining full-sail movement, naval combat, forts and a stylized 3D sea.",
    stack: ["Swift", "SwiftUI", "Metal", "Game Design"],
    platforms: ["iOS"],
    productTypes: ["3D Adventure", "Naval Combat", "Native Game"],
    languages: ["PT-BR", "EN"],
    destinations: [{ label: "Play Skull Sailor", href: "https://skullsailor.com", kind: "web" }],
  },
  {
    domain: "takeaicourse.com",
    title: "TakeAICourse",
    category: "AI education",
    description: "A Next.js and PostgreSQL AI learning platform connecting curriculum, projects, 10,071 prompts, Agents, reusable Skills, progress and Stripe access.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI School", "Learning Platform", "Course Commerce"],
    languages: ["EN"],
    destinations: [
      { label: "Open TakeAICourse", href: "https://takeaicourse.com", kind: "web" },
      { label: "Explore the course library", href: "https://takeaicourse.com/courses", kind: "web" },
      { label: "Open the student dashboard", href: "https://takeaicourse.com/dashboard", kind: "web" },
      { label: "Browse 10,071 prompts", href: "https://takeaicourse.com/prompts", kind: "web" },
    ],
  },
  {
    domain: "talkmaestro.com",
    title: "TalkMaestro",
    category: "AI speaking coach",
    description: "A native SwiftUI speaking coach combining OpenAI analysis, AVFoundation recording and structured local practice state across repeatable coaching sessions.",
    stack: ["Swift", "SwiftUI", "OpenAI", "AVFoundation"],
    platforms: ["iOS"],
    productTypes: ["Speaking Coach", "Realtime Voice AI"],
    languages: ["EN", "ES", "PT-BR", "+5"],
    appStoreUrl: "https://apps.apple.com/br/app/talkmaestro/id6769179963",
    destinations: [
      { label: "Open TalkMaestro", href: "https://talkmaestro.com", kind: "web" },
      { label: "Download for iOS", href: "https://apps.apple.com/br/app/talkmaestro/id6769179963", kind: "ios" },
    ],
  },
  {
    domain: "uglyprofitable.com",
    title: "UglyProfitable",
    category: "Business intelligence",
    description: "A Next.js and PostgreSQL decision platform that normalizes 342 business models into searchable economics, comparisons, state context and Stripe-backed workspaces.",
    stack: ["Next.js", "TypeScript", "PostgreSQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Business Research", "Operator Workspace"],
    languages: ["EN"],
    destinations: [
      { label: "Open UglyProfitable", href: "https://uglyprofitable.com", kind: "web" },
      { label: "Browse all 342 businesses", href: "https://uglyprofitable.com/businesses", kind: "web" },
      { label: "Open the operator workspace", href: "https://uglyprofitable.com/workspace", kind: "web" },
      { label: "See personalized analysis", href: "https://uglyprofitable.com/consultations", kind: "web" },
    ],
  },
  {
    domain: "vendakit.com",
    title: "VendaKit",
    category: "Commerce automation",
    description: "A Next.js marketplace asset pipeline combining OpenAI image editing, PostgreSQL-backed jobs, multiformat exports and Stripe Pix credits.",
    stack: ["Next.js", "PostgreSQL", "OpenAI", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Marketplace Asset Studio", "AI Image & Copy", "Commerce"],
    languages: ["PT-BR"],
    destinations: [
      { label: "Open VendaKit", href: "https://vendakit.com", kind: "web" },
      { label: "Create a sales kit", href: "https://vendakit.com/criar", kind: "web" },
      { label: "See 10 transformations", href: "https://vendakit.com/exemplos", kind: "web" },
      { label: "Open the kit dashboard", href: "https://vendakit.com/dashboard", kind: "web" },
    ],
  },
  {
    domain: "wobistro.com",
    title: "Wobistro",
    category: "Restaurant commerce SaaS",
    description: "A multitenant Laravel and MySQL platform connecting branded storefronts, QR menus, POS, ordering, tables, delivery workflows and Stripe-backed subscriptions.",
    stack: ["Laravel", "PHP", "MySQL", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["Restaurant SaaS", "Ordering & POS", "Multitenant Commerce"],
    languages: ["EN"],
    destinations: [
      { label: "Open Wobistro", href: "https://wobistro.com", kind: "web" },
      { label: "Explore live restaurants", href: "https://wobistro.com/templates", kind: "web" },
      { label: "Open restaurant workspace", href: "https://wobistro.com/user/login", kind: "web" },
    ],
  },
  {
    domain: "xagod.com",
    title: "XAGOD",
    category: "Browser micro-MMO",
    description: "A persistent browser micro-MMO built with Three.js, TypeScript and WebSockets, synchronizing nine classes, shared quests and PostgreSQL-backed world state.",
    stack: ["Three.js", "TypeScript", "WebSocket", "PostgreSQL"],
    platforms: ["Web Game", "Mobile Web"],
    productTypes: ["Persistent MMO", "3D Game"],
    languages: ["EN"],
    destinations: [{ label: "Enter XAGOD", href: "https://xagod.com", kind: "web" }],
  },
  {
    domain: "youareearly.com",
    title: "You Are Early",
    category: "AI research platform",
    description: "A Next.js AI research platform using TypeScript and Framer Motion to connect adoption evidence, strategic actions and Stripe commerce in one cinematic interface.",
    stack: ["Next.js", "TypeScript", "Framer Motion", "Stripe"],
    platforms: ["Web App"],
    productTypes: ["AI Research", "Strategic Education", "Commerce"],
    languages: ["EN"],
    destinations: [
      { label: "Open You Are Early", href: "https://youareearly.com", kind: "web" },
      { label: "Explore the research sample", href: "https://youareearly.com/sample.pdf", kind: "web" },
    ],
  },
  {
    domain: "zerotopublic.com",
    title: "Zero to Public",
    category: "Founder platform",
    description: "A React and TypeScript founder platform modeling 50 shipped products as a structured evidence archive, with routed decision workflows, Stripe commerce and Cloudflare delivery.",
    stack: ["React", "TypeScript", "Stripe", "Cloudflare"],
    platforms: ["Web App"],
    productTypes: ["Product Archive", "Founder Research", "Decision Tools"],
    languages: ["EN"],
    destinations: [
      { label: "Open Zero to Public", href: "https://zerotopublic.com", kind: "web" },
      { label: "Explore the operating system", href: "https://zerotopublic.com/#pricing", kind: "web" },
      { label: "Explore decision reviews", href: "https://zerotopublic.com/consultations", kind: "web" },
      { label: "Study sample outputs", href: "https://zerotopublic.com/consultations/samples", kind: "web" },
    ],
  },
];

export const projects: Project[] = rawProjects.map((project, index) => ({
  ...project,
  hue: (index * 47 + 8) % 360,
}));
